import { useEffect, useRef, useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { postConfirmIntent } from '../api/giving'
import { useUI } from '../context/UIContext'

export interface NedarimPayload {
  givingId: number
  firstName: string
  phone: string
  mail: string
  amount: number
  months: number
  paymentType: 'HK' | 'Ragil'
}

interface NedarimMessage {
  Name: string
  Value: unknown
}

interface TransactionResponse {
  Status: string
  Message?: string
  [key: string]: unknown
}

export const NEDARIM_IFRAME_URL =
  'https://matara.pro/nedarimplus/iframe?language=he&Picture=Hide'

export const usePayment = (
  iframeRef: React.RefObject<HTMLIFrameElement | null>,
) => {
  const queryClient = useQueryClient()
  const { setIsVerifying, setDonationSuccess } = useUI()
  const pendingPayload = useRef<NedarimPayload | null>(null)
  const onErrorRef = useRef<((msg: string) => void) | null>(null)
  const onSuccessRef = useRef<(() => void) | null>(null)

  const postNedarim = useCallback(
    (data: object) => {
      iframeRef.current?.contentWindow?.postMessage(data, '*')
    },
    [iframeRef]
  )

  const requestHeight =  useCallback( () => {
    setTimeout(() => {
      postNedarim({ Name: 'GetHeight' })
    }, 200)
  }, [postNedarim])


  const initPayment = useCallback(
    (payload: NedarimPayload, onError: (msg: string) => void, onSuccess?: () => void) => {
      pendingPayload.current = payload
      console.log('initPayment', payload)
      onErrorRef.current = onError
      onSuccessRef.current = onSuccess ?? null
    },
    []
  )

  const sendTransaction = useCallback(() => {
    const payload = pendingPayload.current
    const params = window.campaignParams
    if (!payload) return

    const nedarimPayload = {
      Name: 'FinishTransaction2',
      Value: {
        Mosad: params.mosadId,
        ApiValid: params.apiValid,
        PaymentType: payload.paymentType,
        Currency: '1',
        ...(params.testMode && {
          Zeout: '123456789',
          Street: 'handler',
          City: 'Jerusalem',
        }),
        ...(params.nedarimGroup && {
          Groupe: params.nedarimGroup,
        }),
        FirstName: payload.firstName,
        Phone: payload.phone,
        Mail: payload.mail,
        Amount: payload.amount,
        Tashlumim: payload.paymentType === 'Ragil' ? 1 : payload.months,
        Param1: String(payload.givingId),
        CallBack: params.restUrl + '/callback',
        CallBackMailError: params.errorEmail,
      },
    }
    postNedarim(nedarimPayload)
  }, [postNedarim])

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      let parsed: NedarimMessage
      if (typeof event.data === 'string') {
        try { parsed = JSON.parse(event.data) } catch { return }
      } else {
        parsed = event.data
      }

      const { Name, Value } = parsed ?? {}

      switch (Name) {
        case 'Height': {
          const iframe = iframeRef.current
          const h = parseInt(String(Value), 10)
          if (iframe) {
            iframe.style.height = `${h}px`
          }
          break
        }

        case 'TransactionResponse': {
          const resp = Value as TransactionResponse
          if (resp.Status === 'Error') {
            onErrorRef.current?.(resp.Message ?? 'אירעה שגיאה בתשלום')
            setIsVerifying(false)
          } else {
            const givingId = pendingPayload.current?.givingId
            if (givingId) {
              // Sanitize string values — Nedarim sends unescaped " in Hebrew text (e.g. הו"ק)
              // Replace with the Hebrew gershayim ״ which is visually identical
              const sanitized = Object.fromEntries(
                Object.entries(resp).map(([k, v]) => [
                  k,
                  typeof v === 'string' ? v.replace(/"/g, '״') : v,
                ])
              )
              postConfirmIntent(givingId, sanitized).catch(() => {})
            }
            onSuccessRef.current?.()
            setIsVerifying(false)
            setDonationSuccess(true)
            queryClient.invalidateQueries({ queryKey: ['campaign-stats'] })
            queryClient.invalidateQueries({ queryKey: ['donations'] })
          }
          break
        }
      }
    }

    window.addEventListener('message', handler)
    return () => window.removeEventListener('message', handler)
  }, [iframeRef, queryClient, setIsVerifying, setDonationSuccess])

  return { initPayment, sendTransaction, requestHeight }
}
